import React, { useEffect, useState } from "react";
import { AppNoInfo, AppRemixIcon } from "@/layout/AppComponents";
import styled from "styled-components";
import { Scrollbars } from "react-custom-scrollbars";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactDOMServer from "react-dom/server";
import { axiosPath } from "../../../../../../routing/helpers/router";
import colors from "../../../../../../constants/colors";
import * as currentPageAction from "../../../../../../store/actions/currentPageAction";
import Translator from "../../../../../components/helpers/Translator";
import CalendarNewEvent from "./Calendar/CalendarNewEvent";
import CalendarEditEvent from "./Calendar/CalendarEditEvent";
import CalendarNextDateSettings from "./Calendar/CalendarNextDateSettings";

const CalendarTab = (props) => {
  const { plantObligationID } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.permissions
  );
  const userBridgePermissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.userBridge?.permissions
  );
  const calendarDates = useSelector(
    (s) => s.currentPage?.calendarTabs?.[plantObligationID]
  );
  const obligationPeriodicity = useSelector(
    (s) =>
      s.currentPage?.plantObligationLevels?.level2?.[plantObligationID]
        ?.obligation?.periodicity
  );
  const poNextDateSettings = useSelector(
    (state) =>
      state.currentPage?.plantObligationLevels?.level3?.[plantObligationID]
        ?.calendar?.nextDateSettings
  );
  const poLevel2 = useSelector(
    (state) => state.currentPage?.plantObligationLevels?.level2
  );
  const [showAddNewCalendarDate, setShowAddNewCalendarDate] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isNextDateSettingsMode, setIsNextDateSettingsMode] = useState(false);

  // > LOAD CALENDAR TAB
  useEffect(() => {
    (async () => {
      try {
        const response = await axios.post(
          axiosPath("api_r_user_module_legalCompliance_calendar_tab"),
          {
            moduleBridgeHex,
            plantHex,
            plantObligationID,
          }
        );
        dispatch(
          currentPageAction.change("calendarTabs", {
            [plantObligationID]: response.data,
          })
        );
      } catch (err) {}
    })();
  }, []);
  // < LOAD CALENDAR TAB

  // < RESOLVE SIMPLECALENDAR
  const handleResolveSimpleCalendar = async (values) => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_legalCompliance_calendar_solveEvent"),
        {
          moduleBridgeHex,
          plantHex,
          plantObligationID,
          calendarDateID: selectedCalendarDate.id,
        }
      );
      dispatch(
        currentPageAction.change("calendarTabs", {
          [plantObligationID]: response.data,
        })
      );
      setSelectedCalendarDate(false);
    } catch (err) {}
  };
  // > RESOLVE SIMPLECALENDAR

  if (!calendarDates) return <AppNoInfo minHeight={300} />;

  return (
    <CalendarTabContainer>
      <SidebarMenu>
        <>
          {(userBridgePermissions?.level !== 2 ||
            userBridgePermissions?.writeCalendar === true) && (
            <span
              className="add-button"
              onClick={() => {
                setShowAddNewCalendarDate(true);
                setSelectedCalendarDate(false);
              }}
            >
              <Translator file="module-legalCompliance" name="new.event" />
            </span>
          )}

          {calendarDates.length > 0 ? (
            <>
              <h3 className="event-type">
                <Translator
                  file="module-legalCompliance"
                  name="pending.events"
                />
              </h3>
              <Scrollbars style={{ height: "110px" }}>
                <ul>
                  {calendarDates[0].length > 0 ? (
                    calendarDates[0].map((cd) => (
                      <li
                        key={cd.id}
                        className={
                          selectedCalendarDate &&
                          selectedCalendarDate.id === cd.id
                            ? "ev-selected"
                            : "text-danger"
                        }
                        onClick={() => {
                          setShowAddNewCalendarDate(false);
                          setSelectedCalendarDate(cd);
                          setIsEditMode(false);
                          setIsNextDateSettingsMode(false);
                        }}
                      >
                        <div>
                          <AppRemixIcon
                            className="mr-2"
                            icon="alert"
                            color={colors.danger}
                          />
                          <span className="cd-date">{cd.date}</span>
                        </div>
                        <div>
                          <AppRemixIcon
                            className="mr-2 float-right"
                            icon="chat-4"
                            color={colors.primary}
                            filled={!!cd.comment}
                            title={cd.comment || ""}
                          />
                          {!permissions?.gcl?.module?.plant?.calendar
                            ?.fullCalendar && (
                            <AppRemixIcon
                              className="mr-2 float-right"
                              icon="attachment-2"
                              noVariants
                              color={
                                cd.evidences.length > 0
                                  ? colors.primary
                                  : colors.grey400
                              }
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="attached.evidences"
                                />
                              )}
                            />
                          )}
                        </div>
                      </li>
                    ))
                  ) : (
                    <AppNoInfo
                      text={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="without.events"
                        />
                      )}
                      color={colors.primaryLight}
                      separation={0}
                      icon={
                        <AppRemixIcon
                          size={24}
                          icon="calendar-check"
                          color={colors.primaryLight}
                        />
                      }
                    />
                  )}
                </ul>
              </Scrollbars>
              <h3 className="event-type">
                <Translator
                  file="module-legalCompliance"
                  name="future.events"
                />
              </h3>
              <Scrollbars style={{ height: "110px" }}>
                <ul>
                  {calendarDates[1].length > 0 ? (
                    calendarDates[1].map((cd) => (
                      <li
                        key={cd.id}
                        className={
                          selectedCalendarDate &&
                          selectedCalendarDate.id === cd.id
                            ? "ev-selected"
                            : ""
                        }
                        onClick={() => {
                          setShowAddNewCalendarDate(false);
                          setSelectedCalendarDate(cd);
                          setIsEditMode(false);
                          setIsNextDateSettingsMode(false);
                        }}
                      >
                        <div>
                          <AppRemixIcon className="mr-2" icon="time" />
                          <span className="cd-date">{cd.date}</span>
                        </div>
                        <div>
                          <AppRemixIcon
                            className="mr-2 float-right"
                            icon="chat-4"
                            color={colors.primary}
                            filled={!!cd.comment}
                            title={cd.comment || ""}
                          />
                          {!permissions?.gcl?.module?.plant?.calendar
                            ?.fullCalendar && (
                            <AppRemixIcon
                              className="mr-2 float-right"
                              icon="attachment-2"
                              noVariants
                              color={
                                cd.evidences.length > 0
                                  ? colors.primary
                                  : colors.grey400
                              }
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="attached.evidences"
                                />
                              )}
                            />
                          )}
                        </div>
                      </li>
                    ))
                  ) : (
                    <AppNoInfo
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="without.events"
                        />
                      )}
                      color={colors.primaryLight}
                      separation={0}
                      icon={
                        <AppRemixIcon
                          size={24}
                          icon="calendar-check"
                          color={colors.primaryLight}
                        />
                      }
                    />
                  )}
                </ul>
              </Scrollbars>
              <h3 className="event-type">
                <Translator
                  file="module-legalCompliance"
                  name="solved.events"
                />
              </h3>
              <Scrollbars style={{ height: "110px" }}>
                <ul>
                  {calendarDates[2].length > 0 ? (
                    calendarDates[2].map((cd) => (
                      <li
                        key={cd.id}
                        className={
                          selectedCalendarDate &&
                          selectedCalendarDate.id === cd.id
                            ? "ev-selected"
                            : "text-success"
                        }
                        onClick={() => {
                          setShowAddNewCalendarDate(false);
                          setSelectedCalendarDate(cd);
                          setIsEditMode(false);
                          setIsNextDateSettingsMode(false);
                        }}
                      >
                        <div>
                          <AppRemixIcon
                            className="mr-2"
                            icon="check"
                            color={colors.success}
                          />
                          <span className="cd-date">{cd.date}</span>
                        </div>
                        <div>
                          <AppRemixIcon
                            className="mr-2 float-right"
                            icon="chat-4"
                            color={colors.primary}
                            filled={!!cd.comment}
                            title={cd.comment || ""}
                          />
                          {!permissions?.gcl?.module?.plant?.calendar
                            ?.fullCalendar && (
                            <AppRemixIcon
                              className="mr-2 float-right"
                              icon="attachment-2"
                              noVariants
                              color={
                                cd.evidences.length > 0
                                  ? colors.primary
                                  : colors.grey400
                              }
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="attached.evidences"
                                />
                              )}
                            />
                          )}
                        </div>
                      </li>
                    ))
                  ) : (
                    <AppNoInfo
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="without.events"
                        />
                      )}
                      color={colors.primaryLight}
                      separation={0}
                      icon={
                        <AppRemixIcon
                          size={24}
                          icon="calendar-check"
                          color={colors.primaryLight}
                        />
                      }
                    />
                  )}
                </ul>
              </Scrollbars>
            </>
          ) : (
            <AppNoInfo
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-legalCompliance"
                  name="without.events"
                />
              )}
              color={colors.primaryLight}
              icon={
                <AppRemixIcon
                  size={24}
                  icon="file-search"
                  color={colors.primaryLight}
                />
              }
            />
          )}
        </>
      </SidebarMenu>
      <Content>
        {showAddNewCalendarDate ? (
          <CalendarNewEvent
            plantObligationID={plantObligationID}
            onCreated={() => setShowAddNewCalendarDate(false)}
          />
        ) : (
          <>
            {selectedCalendarDate ? (
              <div>
                {isEditMode ? (
                  <CalendarEditEvent
                    plantObligationID={plantObligationID}
                    selectedEvent={selectedCalendarDate}
                    onCreated={(r) => {
                      setSelectedCalendarDate(null);
                      setIsEditMode(false);
                      setIsNextDateSettingsMode(false);
                    }}
                  />
                ) : (
                  <>
                    <h3>
                      <Translator
                        file="module-legalCompliance"
                        name="event.information"
                      />{" "}
                      {selectedCalendarDate.date}
                      <span
                        className="float-right pointer mr-2"
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="close"
                          />
                        )}
                      >
                        <AppRemixIcon
                          icon="close"
                          size={16}
                          onClick={() => {
                            setSelectedCalendarDate(null);
                            setIsEditMode(false);
                            setIsNextDateSettingsMode(false);
                          }}
                        />
                      </span>
                      {(userBridgePermissions?.level !== 2 ||
                        userBridgePermissions?.reports?.export === true) && (
                        <span
                          className="float-right pointer mr-2"
                          title={ReactDOMServer.renderToString(
                            <Translator file="general" name="general.edit" />
                          )}
                        >
                          <AppRemixIcon
                            icon="edit"
                            size={16}
                            onClick={() => setIsEditMode(true)}
                          />
                        </span>
                      )}
                    </h3>
                    <EventInfo className="mt-4">
                      {permissions?.gcl?.module?.plant?.calendar
                        ?.fullCalendar && (
                        <>
                          <li>
                            <span className="eventinfo-title">
                              <Translator
                                file="module-legalCompliance"
                                name="type"
                              />
                            </span>
                            <span className="eventinfo-content">
                              {selectedCalendarDate.type === 0 ? (
                                <Translator
                                  file="module-legalCompliance"
                                  name="module.lc.nextDate-yes"
                                />
                              ) : (
                                <Translator
                                  file="module-legalCompliance"
                                  name="created.by.user"
                                />
                              )}
                            </span>
                          </li>
                          <li>
                            <span className="eventinfo-title">
                              <Translator
                                file="module-legalCompliance"
                                name="module.lc.nextDate"
                              />
                            </span>
                            <span className="eventinfo-content">
                              {selectedCalendarDate.type === 0 ? (
                                obligationPeriodicity
                              ) : (
                                <Translator
                                  file="module-legalCompliance"
                                  name="module.lc.no-nextDate"
                                />
                              )}
                            </span>
                          </li>
                        </>
                      )}
                      {permissions?.gcl?.module?.plant?.states
                        ?.calendarStateChange && (
                        <li>
                          <span className="eventinfo-title">
                            <Translator
                              file="module-legalCompliance"
                              name="action"
                            />
                          </span>
                          <span className="eventinfo-content">
                            <Translator
                              file="module-legalCompliance"
                              name="obligation.will.change.unfulfilled.if.no.resolve"
                            />
                          </span>
                        </li>
                      )}
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="attached.evidences"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedCalendarDate.evidences.length > 0 ? (
                            selectedCalendarDate.evidences.map((e) => (
                              <div key={e.id}>{e.name}</div>
                            ))
                          ) : (
                            <span>
                              <Translator
                                file="module-legalCompliance"
                                name="no.attached.evidences"
                              />
                            </span>
                          )}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="comment"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedCalendarDate.comment ? (
                            selectedCalendarDate.comment
                          ) : (
                            <span className="text-muted">
                              <Translator
                                file="module-legalCompliance"
                                name="no.comment"
                              />
                            </span>
                          )}
                        </span>
                      </li>
                      {(!permissions?.gcl?.module?.plant?.calendar
                        ?.fullCalendar ||
                        permissions?.gcl?.module?.plant?.calendar
                          ?.fullCalendar === false) &&
                        (userBridgePermissions?.level !== 2 ||
                          userBridgePermissions?.reports?.export === true) && (
                          <>
                            <li>
                              <span className="eventinfo-title">
                                <Translator file="user" name="user.state" />
                              </span>
                              <span className="eventinfo-content">
                                {selectedCalendarDate.resolved ? (
                                  <Translator
                                    file="module-legalCompliance"
                                    name="solved"
                                  />
                                ) : (
                                  <Translator
                                    file="module-legalCompliance"
                                    name="pending"
                                  />
                                )}
                              </span>
                            </li>
                            {selectedCalendarDate.resolved ? (
                              <span
                                className="btn btn-danger float-right"
                                onClick={() => handleResolveSimpleCalendar()}
                              >
                                <Translator
                                  file="module-legalCompliance"
                                  name="mark.as.pending"
                                />
                              </span>
                            ) : (
                              <span
                                className="btn btn-success float-right"
                                onClick={() => handleResolveSimpleCalendar()}
                              >
                                <Translator
                                  file="module-legalCompliance"
                                  name="mark.as.resolved"
                                />
                              </span>
                            )}
                          </>
                        )}
                    </EventInfo>
                  </>
                )}
              </div>
            ) : isNextDateSettingsMode ? (
              <>
                <CalendarNextDateSettings
                  plantObligationID={plantObligationID}
                  onChange={(r) => {
                    setSelectedCalendarDate(null);
                    setIsEditMode(false);
                    setIsNextDateSettingsMode(false);
                  }}
                />
              </>
            ) : (
              <AppNoInfo
                text={
                  <>
                    <span>
                      <Translator
                        file="module-legalCompliance"
                        name="select.event.or.create.pressing.button.new.event"
                        isHtml
                      />
                    </span>
                    <br />
                    {permissions?.gcl?.module?.plant?.calendar?.fullCalendar &&
                      [1, 2].includes(
                        poLevel2?.[plantObligationID]?.obligation?.nextDateType
                      ) && (
                        <>
                          {!poNextDateSettings ? (
                            <span
                              className="btn btn-warning mt-5"
                              onClick={() => setIsNextDateSettingsMode(true)}
                            >
                              <AppRemixIcon
                                icon="settings-2"
                                className="mr-2"
                              />
                              <Translator
                                file="module-legalCompliance"
                                name="periodicity.setting.needed"
                              />
                            </span>
                          ) : (
                            <span
                              className="btn btn-outline-info btn-sm mt-5"
                              onClick={() => setIsNextDateSettingsMode(true)}
                            >
                              <AppRemixIcon
                                icon="settings-2"
                                className="mr-2"
                              />
                              <Translator
                                file="module-legalCompliance"
                                name="set.periodicity"
                              />
                            </span>
                          )}
                        </>
                      )}
                  </>
                }
                color={colors.primaryLight}
                icon={
                  <AppRemixIcon
                    size={40}
                    icon="calendar-check"
                    color={colors.primaryLight}
                  />
                }
              />
            )}
          </>
        )}
      </Content>
    </CalendarTabContainer>
  );
};
export default CalendarTab;

const CalendarTabContainer = styled.div`
  height: 400px;
  display: grid;
  grid-template-columns: 350px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  .event-type {
    margin: 4px 0 2px 6px;
    font-size: 13px;
    text-transform: uppercase;
  }
  .add-button {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${colors.primary};
    color: ${colors.primary};
    margin: 0;
    height: 32px;
    cursor: pointer;
  }
  ul {
    padding: 4px;
    //height: 268px;
    overflow-y: auto;
    li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 2px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;

      &.ev-selected {
        background: ${colors.grey300};
      }
      .cd-date {
        font-size: 12px;
      }
    }
    li:last-child {
      border-bottom: 0;
    }
  }
`;

const Content = styled.div`
  overflow-y: auto;
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  height: 400px;
  .row-no-margin > .row {
    margin: 0;
  }
`;

const EventInfo = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    padding: 8px 0;
    border-top: 1px solid ${colors.grey300};
    display: flex;
    align-items: baseline;
    .eventinfo-title {
      display: inline-block;
      width: 200px;
      text-transform: uppercase;
      font-weight: bold;
      color: ${colors.primary};
    }
  }
`;
